import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import ConfirmModal from '../../components/ConfirmModal';
import { resolveImageUrl, PROFILE_PLACEHOLDER } from '../../utils/imageHelper';

const AdminProfiles = () => {
  const [profiles, setProfiles]       = useState([]);
  const [filtered, setFiltered]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [genderFilter, setGenderFilter] = useState('All');
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting]       = useState(false);
  const { success, error }            = useToast();

  const fetchProfiles = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/matrimony/profiles');
      setProfiles(Array.isArray(data) ? data : []);
    } catch (err) {
      error('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  useEffect(() => {
    let result = profiles;
    if (search.trim()) {
      result = result.filter(p =>
        p.fullName?.toLowerCase().includes(search.toLowerCase()) ||
        p.currentResidence?.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (genderFilter !== 'All') result = result.filter(p => p.gender === genderFilter);
    setFiltered(result);
  }, [profiles, search, genderFilter]);

  const handleDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/profiles/${deleteModal.id}`);
      success('Profile deleted successfully');
      setDeleteModal(null);
      fetchProfiles();
    } catch (err) {
      error('Failed to delete profile');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout title="Matrimony Profiles">
      <div className="ap-header">
        <div className="ap-filters">
          <input
            type="text" className="form-input"
            placeholder="Search by name or city..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '280px' }}
          />
          <select className="form-select" value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)} style={{ width: '140px' }}>
            <option value="All">All Genders</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
          <span className="ap-count">{filtered.length} profiles</span>
        </div>
      </div>

      <div className="admin-card">
        {loading ? (
          <div className="ap-center">Loading profiles...</div>
        ) : filtered.length > 0 ? (
          <div className="profiles-grid">
            {filtered.map((profile) => (
              <div key={profile.id} className="pac">
                <div className="pac__photo">
                  <img
                    src={resolveImageUrl(profile.profilePhotoUrl, PROFILE_PLACEHOLDER)}
                    alt={profile.fullName}
                    onError={(e) => { e.target.src = PROFILE_PLACEHOLDER; }}
                  />
                </div>
                <div className="pac__info">
                  <div className="pac__name">{profile.fullName}</div>
                  <div className="pac__meta">{profile.gender} • {profile.age} yrs</div>
                  <div className="pac__meta">📍 {profile.currentResidence || '—'}</div>
                  <div className="pac__meta">🎓 {profile.education || '—'}</div>
                </div>
                <div className="pac__actions">
                  <Link to={`/matrimony/${profile.id}`} target="_blank" className="ap-btn ap-btn--view">👁️ View</Link>
                  <button className="ap-btn ap-btn--delete" onClick={() => setDeleteModal(profile)}>🗑️ Delete</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="ap-center">
            <div style={{ fontSize: '3rem', marginBottom: '8px' }}>💑</div>
            <p>No profiles found.</p>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        onConfirm={handleDelete}
        title="Delete Profile"
        message={`Delete profile of "${deleteModal?.fullName}"? This cannot be undone.`}
        confirmText="Delete"
        type="danger"
        loading={deleting}
      />

      <style jsx>{`
        .ap-header { margin-bottom:var(--space-6); }
        .ap-filters { display:flex; gap:var(--space-3); align-items:center; flex-wrap:wrap; }
        .ap-count { color:var(--neutral-600); font-size:var(--text-sm); margin-left:auto; }
        .admin-card { background:white; border-radius:var(--radius-xl); padding:var(--space-6); border:1px solid var(--neutral-200); box-shadow:var(--shadow-sm); }
        .profiles-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:var(--space-4); }
        .pac { display:flex; align-items:center; gap:var(--space-3); padding:var(--space-4); border:1px solid var(--neutral-200); border-radius:var(--radius-xl); background:var(--neutral-50); transition:all var(--transition-fast); }
        .pac:hover { border-color:var(--primary-200); background:var(--primary-50); box-shadow:var(--shadow-sm); }
        .pac__photo { width:52px; height:52px; border-radius:50%; overflow:hidden; flex-shrink:0; background:var(--neutral-200); }
        .pac__photo img { width:100%; height:100%; object-fit:cover; }
        .pac__info { flex:1; overflow:hidden; }
        .pac__name { font-size:var(--text-sm); font-weight:var(--weight-semibold); color:var(--neutral-900); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .pac__meta { font-size:var(--text-xs); color:var(--neutral-600); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .pac__actions { display:flex; flex-direction:column; gap:var(--space-1); flex-shrink:0; }
        .ap-btn { padding:var(--space-1) var(--space-3); border:none; border-radius:var(--radius-md); font-size:var(--text-xs); font-weight:var(--weight-semibold); cursor:pointer; transition:all var(--transition-fast); font-family:var(--font-body); text-decoration:none; text-align:center; white-space:nowrap; }
        .ap-btn--view   { background:var(--primary-100); color:var(--primary-700); }
        .ap-btn--view:hover   { background:var(--primary-200); }
        .ap-btn--delete { background:var(--error-light); color:var(--error); }
        .ap-btn--delete:hover { background:#fecaca; }
        .ap-center { text-align:center; padding:var(--space-12); color:var(--neutral-500); }
      `}</style>
    </AdminLayout>
  );
};

export default AdminProfiles;